import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AiOutlineClose } from 'react-icons/ai';
import { adsServer } from '../../server';
import Loader from '../Layout/Loader';
import { toast } from 'react-toastify';
import ShopAdSetModal from './ShopAdSetModal';

const zeroRange = { min: 0, max: 0 };

const getCommunityImage = (community) => {
  return community?.image || community?.banner?.url || community?.avatar?.url || '';
};

const getDailySpendFromDetails = (details) => {
  const duration = Number(details?.adDuration) || 0;
  const totalSpend = Number(details?.adSpend) || 0;

  if (duration > 0 && totalSpend > 0) {
    return Math.max(1, Math.round(totalSpend / duration));
  }

  return 50;
};

const SponsorCommunityCampaign = ({ onSave, adType, details = {}, adminMode = false, adminOptions = {} }) => {
  const [selectedCommunityId, setSelectedCommunityId] = useState(details.communityId || '');
  const [audienceScope, setAudienceScope] = useState(details.audienceScope || 'both');
  const [adSpendPerDay, setAdSpendPerDay] = useState(getDailySpendFromDetails(details));
  const [adDuration, setAdDuration] = useState(details.adDuration || 7);
  const [totalAdSpend, setTotalAdSpend] = useState((getDailySpendFromDetails(details) || 50) * (details.adDuration || 7));
  const [predictedClicks, setPredictedClicks] = useState(zeroRange);
  const [predictedImpressions, setPredictedImpressions] = useState(zeroRange);
  const [displayedPredictedClicks, setDisplayedPredictedClicks] = useState(zeroRange);
  const [displayedPredictedImpressions, setDisplayedPredictedImpressions] = useState(zeroRange);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdSetModalOpen, setIsAdSetModalOpen] = useState(false);
  const [adSet, setAdSet] = useState(details.adSet || {});
  const [isAdSetSaved, setIsAdSetSaved] = useState(Boolean(details.adSet));
  const [activeTimeFrame, setActiveTimeFrame] = useState('total');
  const [predictionMessage, setPredictionMessage] = useState('No ad prediction data available yet.');
  const debounceRef = useRef(null);

  const communities = useMemo(() => adminOptions.communities || [], [adminOptions.communities]);

  const visibleCommunities = useMemo(() => {
    if (audienceScope === 'both') {
      return communities;
    }

    return communities.filter((community) => String(community.privacy || 'public') === audienceScope);
  }, [audienceScope, communities]);

  const selectedCommunity = useMemo(
    () => communities.find((community) => String(community._id) === String(selectedCommunityId)),
    [communities, selectedCommunityId]
  );

  useEffect(() => {
    if (!selectedCommunityId) {
      return;
    }

    const hasVisibleSelection = visibleCommunities.some(
      (community) => String(community._id) === String(selectedCommunityId)
    );

    if (!hasVisibleSelection && audienceScope !== 'both') {
      setSelectedCommunityId('');
      setIsAdSetSaved(false);
      setAdSet({});
    }
  }, [audienceScope, selectedCommunityId, visibleCommunities]);

  useEffect(() => {
    const nextDailySpend = getDailySpendFromDetails(details);
    const nextDuration = details.adDuration || 7;
    setSelectedCommunityId(details.communityId || '');
    setAudienceScope(details.audienceScope || 'both');
    setAdSpendPerDay(nextDailySpend);
    setAdDuration(nextDuration);
    setTotalAdSpend(nextDailySpend * nextDuration);
    setAdSet(details.adSet || {});
    setIsAdSetSaved(Boolean(details.adSet));
  }, [details]);

  useEffect(() => {
    setTotalAdSpend(adSpendPerDay * adDuration);
  }, [adSpendPerDay, adDuration]);

  useEffect(() => {
    fetchPredictions(adSpendPerDay * adDuration);
  }, [adSpendPerDay, adDuration]);

  const updateDisplayValues = (mode, clicksRange = predictedClicks, impressionsRange = predictedImpressions) => {
    const factor = mode === 'daily' ? 1 / adDuration : 1;
    setDisplayedPredictedClicks({
      min: Math.round(clicksRange.min * factor),
      max: Math.round(clicksRange.max * factor),
    });
    setDisplayedPredictedImpressions({
      min: Math.round(impressionsRange.min * factor),
      max: Math.round(impressionsRange.max * factor),
    });
  };

  const fetchPredictions = async (spend) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${adsServer}/sponsored/predict`,
          { adSpend: spend },
          { withCredentials: true }
        );

        if (data?.hasHistoricalData === false) {
          setPredictedClicks(zeroRange);
          setPredictedImpressions(zeroRange);
          updateDisplayValues('total', zeroRange, zeroRange);
          setPredictionMessage(data.message || 'No ad prediction data available yet.');
          return;
        }

        if (data) {
          setPredictedClicks(data.predictedClicks);
          setPredictedImpressions(data.predictedImpressions);
          updateDisplayValues('total', data.predictedClicks, data.predictedImpressions);
          setPredictionMessage('');
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.response?.data || '';
        if (error.response?.status === 404 || /No historical data available/i.test(String(errorMessage))) {
          setPredictedClicks(zeroRange);
          setPredictedImpressions(zeroRange);
          updateDisplayValues('total', zeroRange, zeroRange);
          setPredictionMessage('No ad prediction data available yet.');
          return;
        }

        console.error('Error fetching predictions:', error);
        toast.error('Failed to fetch predictions');
      }
    }, 500);
  };

  const handleDisplayModeChange = (mode) => {
    setActiveTimeFrame(mode);
    updateDisplayValues(mode);
  };

  const handleDeselectCommunity = (event) => {
    event.stopPropagation();
    setSelectedCommunityId('');
    setIsAdSetSaved(false);
    setAdSet({});
  };

  const openAdSetModal = () => {
    if (!selectedCommunity?._id) {
      toast.error('Please select a community before customizing the ad set.');
      return;
    }

    setIsAdSetModalOpen(true);
  };

  const handleAdSetSave = (newAdSet) => {
    setAdSet(newAdSet);
    setIsAdSetSaved(true);
    setIsAdSetModalOpen(false);
  };

  const handleSaveAd = () => {
    if (!adminMode) {
      toast.error('Community campaign creation is only available in admin mode.');
      return;
    }

    if (!selectedCommunity?._id) {
      toast.error('Please select a community before saving.');
      return;
    }

    const detailsPayload = {
      communityId: selectedCommunity._id,
      communityName: selectedCommunity.name,
      communityHandle: selectedCommunity.handle,
      communityImage: getCommunityImage(selectedCommunity),
      audienceScope,
      adSpend: totalAdSpend,
      adDuration,
      adSet,
      productName: selectedCommunity.name,
    };

    onSave(adSet, detailsPayload, adType);
    toast.success('Community ad configuration saved.');
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="sponsor-brand-component-container sponsor-community-component-container">
      <form>
        <h2 className="sponsor-brand-component-title">Sponsor a Community</h2>

        <div className="sponsor-product-component-slider sponsor-community-component-scope-slider">
          <label>Community Reach</label>
          <div className="sponsor-product-component-display-mode sponsor-community-component-scope-buttons">
            <button
              type="button"
              onClick={() => setAudienceScope('public')}
              className={`time-button ${audienceScope === 'public' ? 'active-time-button' : ''}`}
            >
              Public
            </button>
            <button
              type="button"
              onClick={() => setAudienceScope('private')}
              className={`time-button ${audienceScope === 'private' ? 'active-time-button' : ''}`}
            >
              Private
            </button>
            <button
              type="button"
              onClick={() => setAudienceScope('both')}
              className={`time-button ${audienceScope === 'both' ? 'active-time-button' : ''}`}
            >
              Both
            </button>
          </div>
        </div>

        <div className="sponsor-brand-component-product-selection">
          <label>Select a Community:</label>
          <div className="sponsor-brand-component-product-list sponsor-community-component-list">
            {visibleCommunities.map((community) => {
              const isSelected = String(selectedCommunityId) === String(community._id);

              return (
                <div
                  key={community._id}
                  onClick={() => setSelectedCommunityId(community._id)}
                  className={`sponsor-brand-component-product-item sponsor-community-component-card ${isSelected ? 'selected' : ''}`}
                >
                  <img src={getCommunityImage(community)} alt={community.name} />
                  <div className="sponsor-community-component-copy">
                    <span>{community.name}</span>
                    <small>
                      {(community.privacy || 'public').toUpperCase()} · {community.memberCount || 0} members · {community.postCount || 0} posts
                    </small>
                  </div>
                  {isSelected && (
                    <AiOutlineClose
                      onClick={handleDeselectCommunity}
                      className="sponsor-brand-component-close-icon"
                    />
                  )}
                </div>
              );
            })}
          </div>
          {visibleCommunities.length === 0 && (
            <div className="sponsor-brand-component-null-message">
              No communities match the selected reach filter.
            </div>
          )}
        </div>

        {selectedCommunityId && (
          <div className="customize-ad-set-button-container">
            <button type="button" onClick={openAdSetModal} className="customize-ad-set-button">
              Customize Ad Set
            </button>
          </div>
        )}

        <div className="sponsor-product-component-slider">
          <label>Set Ad Spend per Day ($):</label>
          <input
            type="text"
            value={adSpendPerDay}
            onChange={(event) => {
              const nextSpend = Number(event.target.value);
              setAdSpendPerDay(nextSpend);
              fetchPredictions(nextSpend * adDuration);
            }}
            className="sponsor-brand-component-input"
          />
          <input
            type="range"
            min="1"
            max="1000"
            value={adSpendPerDay}
            onChange={(event) => {
              const nextSpend = Number(event.target.value);
              setAdSpendPerDay(nextSpend);
              fetchPredictions(nextSpend * adDuration);
            }}
            className="sponsor-product-component-range-slider"
          />
        </div>

        <div className="sponsor-product-component-slider">
          <label>Set Ad Duration (Days):</label>
          <input
            type="text"
            value={adDuration}
            onChange={(event) => {
              const nextDuration = Number(event.target.value);
              setAdDuration(nextDuration);
              fetchPredictions(adSpendPerDay * nextDuration);
            }}
            className="sponsor-brand-component-input"
          />
          <input
            type="range"
            min="1"
            max="30"
            value={adDuration}
            onChange={(event) => {
              const nextDuration = Number(event.target.value);
              setAdDuration(nextDuration);
              fetchPredictions(adSpendPerDay * nextDuration);
            }}
            className="sponsor-product-component-range-slider"
          />
        </div>

        <div className="sponsor-brand-component-total-spend">
          <label>Total Ad Spend ($): {totalAdSpend}</label>
        </div>

        <div className="sponsor-product-component-display-mode">
          <button
            type="button"
            onClick={() => handleDisplayModeChange('daily')}
            className={`time-button ${activeTimeFrame === 'daily' ? 'active-time-button' : ''}`}
          >
            Daily
          </button>
          <button
            type="button"
            onClick={() => handleDisplayModeChange('total')}
            className={`time-button ${activeTimeFrame === 'total' ? 'active-time-button' : ''}`}
          >
            Total
          </button>
        </div>

        <div className="sponsor-brand-component-predictions">
          {displayedPredictedClicks.min !== 0 || displayedPredictedClicks.max !== 0 ? (
            <>
              <div className="sponsor-brand-component-prediction-item">
                <label>Predicted Clicks Range:</label>
                <span>{displayedPredictedClicks.min} to {displayedPredictedClicks.max}</span>
              </div>
              <div className="sponsor-brand-component-prediction-item">
                <label>Predicted Impressions Range:</label>
                <span>{displayedPredictedImpressions.min} to {displayedPredictedImpressions.max}</span>
              </div>
            </>
          ) : (
            <div className="sponsor-brand-component-null-message">
              {predictionMessage}
            </div>
          )}
        </div>

        <button
          type="button"
          className={`sponsor-brand-component-submit-button ${isAdSetSaved && selectedCommunityId ? 'enabled' : 'disabled'}`}
          onClick={isAdSetSaved && selectedCommunityId ? handleSaveAd : null}
          disabled={!isAdSetSaved || !selectedCommunityId}
        >
          Save Ad Configuration
        </button>
      </form>

      <ShopAdSetModal
        key={selectedCommunityId || 'community-ad-set'}
        isOpen={isAdSetModalOpen}
        onClose={() => setIsAdSetModalOpen(false)}
        onSave={handleAdSetSave}
        adFormats={[
          'Top Banner Ad',
          'Top Banner Mobile Ad',
          'Banner Ad',
          'Banner Mobile Ad',
          'Sponsored Shops',
          'Sponsored Shop Carousel',
          'Dual Carousel',
          'Quad Carousel',
          'Sponsored Feed Ads',
        ]}
        shopImage={getCommunityImage(selectedCommunity)}
        shopName={selectedCommunity?.name}
        shopAvatar={selectedCommunity?.avatar?.url}
      />
    </div>
  );
};

export default SponsorCommunityCampaign;